/**
 * Plan somebody onto a project.
 *
 * Entered in **days a week**, stored in minutes. Days is how the commitment is
 * actually agreed — "two days a week on this" — and minutes is what every
 * downstream sum works in. The conversion happens once, here.
 *
 * The person and the project are fixed once the allocation exists: changing
 * either is not an edit, it is a different plan. So the edit form carries the
 * dates and the hours, and says who and what it is about.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Alert,
  ApiError,
  Button,
  Input,
  Modal,
  RecordSelect,
  Textarea,
  api,
  toast,
  todayISO,
} from '@connect/web-shared';

const schema = z
  .object({
    starts_on: z.string().min(1, 'When does it start?'),
    ends_on: z.string().min(1, 'And when does it end?'),
    days_per_week: z
      .string()
      .trim()
      .regex(/^[0-5](\.\d)?$/, 'Days a week, to one decimal. At most 5.')
      .refine((value) => Number(value) > 0, 'More than nothing.'),
    notes: z.string().trim().max(500).optional(),
  })
  .refine((values) => values.ends_on >= values.starts_on, {
    path: ['ends_on'],
    message: 'It cannot end before it starts.',
  });

type FormValues = z.infer<typeof schema>;

interface AllocationRecord {
  public_id: string;
  project_public_id: string;
  project_name: string;
  employee_public_id: string;
  employee_name: string;
  starts_on: string;
  ends_on: string;
  minutes_per_week: number;
  notes: string | null;
}

// A FUNCTION, not a constant. A module-level object would capture `todayISO()`
// once, when the bundle loads — so a console left open overnight would go on
// offering yesterday's date, and the entry would be filed against the wrong
// day by somebody who never looked at the field.
const DEFAULTS = (): FormValues => ({
  starts_on: todayISO(),
  ends_on: '',
  days_per_week: '2',
  notes: '',
});

/** A working day, in minutes. The same figure the API uses for its ceiling. */
const DAY_MINUTES = 8 * 60;

export function AllocationDialog({
  open,
  onClose,
  onSaved,
  allocation,
  projectPublicId,
  projectName,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: (publicId: string) => void;
  /** The allocation being edited, or `null` to plan a new one. */
  allocation: AllocationRecord | null;
  projectPublicId?: string;
  projectName?: string;
}) {
  const queryClient = useQueryClient();
  const editing = allocation !== null;
  const [project, setProject] = useState<string | null>(projectPublicId ?? null);
  const [employee, setEmployee] = useState<string | null>(null);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS() });

  useEffect(() => {
    if (!open) return;
    if (allocation) {
      form.reset({
        starts_on: allocation.starts_on,
        ends_on: allocation.ends_on,
        days_per_week: String(allocation.minutes_per_week / DAY_MINUTES),
        notes: allocation.notes ?? '',
      });
      setProject(allocation.project_public_id);
      setEmployee(allocation.employee_public_id);
    } else {
      form.reset(DEFAULTS());
      setProject(projectPublicId ?? null);
      setEmployee(null);
    }
  }, [open, allocation, form, projectPublicId]);

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const minutes = Math.round(Number(values.days_per_week) * DAY_MINUTES);
      const body = {
        starts_on: values.starts_on,
        ends_on: values.ends_on,
        minutes_per_week: minutes,
        notes: values.notes?.trim() || null,
      };
      if (editing) return api.patch(`/allocations/${allocation.public_id}`, body);
      return api.post<{ public_id: string }>('/allocations', {
        ...body,
        project_id: project,
        employee_id: employee,
      });
    },
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ['allocations'] });
      toast.success(editing ? 'Allocation updated' : 'Planned in');
      onSaved?.((record as { public_id?: string }).public_id ?? allocation?.public_id ?? '');
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        let placed = false;
        for (const [field, message] of Object.entries(error.fieldErrors)) {
          if (field in DEFAULTS()) {
            form.setError(field as keyof FormValues, { message });
            placed = true;
          }
        }
        // The capacity refusal names the total and the overlapping rows. It is
        // about the request as a whole, so it goes on the form as a whole.
        if (!placed) form.setError('root', { message: error.message });
        return;
      }
      form.setError('root', { message: 'Could not reach the server. Try again.' });
    },
  });

  const submit = () => {
    if (!editing && (project === null || employee === null)) {
      form.setError('root', { message: 'Choose the person and the project.' });
      return;
    }
    void form.handleSubmit((values) => save.mutateAsync(values))();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit allocation' : 'Plan someone in'}
      size="md"
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button size="sm" loading={save.isPending} onClick={submit}>
            {editing ? 'Save changes' : 'Plan it'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {form.formState.errors.root?.message ? (
          <Alert tone="danger">{form.formState.errors.root.message}</Alert>
        ) : null}

        {editing ? (
          <Alert tone="info">
            {allocation.employee_name} on {allocation.project_name}. Moving an allocation to
            somebody else is a different plan — remove this one and make it.
          </Alert>
        ) : (
          <>
            <RecordSelect
              label="Who"
              required
              endpoint="/hr/employees"
              value={employee}
              onChange={(next) => setEmployee(next)}
              toOption={(row) => ({
                value: String(row.public_id),
                label: String(row.full_name),
                detail: String(row.job_title ?? ''),
              })}
              placeholder="Search people…"
              hint="Needs the People service."
            />
            <RecordSelect
              label="Project"
              required
              endpoint="/projects"
              value={project}
              valueLabel={projectName}
              onChange={(next) => setProject(next)}
              toOption={(row) => ({
                value: String(row.public_id),
                label: String(row.name),
                detail: String(row.client_name ?? ''),
              })}
              placeholder="Search projects…"
            />
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <Input
            label="From"
            type="date"
            required
            error={form.formState.errors.starts_on?.message}
            {...form.register('starts_on')}
          />
          <Input
            label="Until"
            type="date"
            required
            error={form.formState.errors.ends_on?.message}
            {...form.register('ends_on')}
          />
          <Input
            label="Days a week"
            inputMode="decimal"
            required
            hint="Stored as minutes."
            error={form.formState.errors.days_per_week?.message}
            {...form.register('days_per_week')}
          />
        </div>

        <Textarea
          label="Notes"
          rows={2}
          hint="What they are there for. Read by whoever is planning next month."
          error={form.formState.errors.notes?.message}
          {...form.register('notes')}
        />
      </form>
    </Modal>
  );
}
